import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  useReactTable,
  getCoreRowModel,
  type SortingState,
  type PaginationState,
} from "@tanstack/react-table";
import { DataTable, DataTablePagination } from "@repo/ui";
import { createActivityColumns } from "./activity-columns";
import type { ActivityTransaction } from "@/lib/types/activity";
import { DrugDetailsDrawer } from "@/components/inventory/drug-details-drawer";
import { NotesThread } from "@/components/notes-thread";

export interface ActivityTableProps {
  transactions: ActivityTransaction[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  isLoading: boolean;
  onView: (transaction: ActivityTransaction) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export function ActivityTable({
  transactions,
  pagination,
  isLoading,
  onView,
  onPageChange,
  onPageSizeChange,
}: ActivityTableProps) {
  const navigate = useNavigate();

  const [sorting, setSorting] = React.useState<SortingState>([
    { id: "transaction_date", desc: true },
  ]);

  const [drugDetailsDrawerOpen, setDrugDetailsDrawerOpen] =
    React.useState(false);
  const [selectedNDC, setSelectedNDC] = React.useState<string | null>(null);

  // Notes thread state
  const [notesOpen, setNotesOpen] = React.useState(false);
  const [notesTransaction, setNotesTransaction] =
    React.useState<ActivityTransaction | null>(null);

  const handleNDCClick = React.useCallback((ndc: string) => {
    setSelectedNDC(ndc);
    setDrugDetailsDrawerOpen(true);
  }, []);

  const handleEditNotes = React.useCallback(
    (transaction: ActivityTransaction) => {
      setNotesTransaction(transaction);
      setNotesOpen(true);
    },
    []
  );

  const [{ pageIndex, pageSize }, setPaginationState] =
    React.useState<PaginationState>({
      pageIndex: (pagination?.page || 1) - 1,
      pageSize: pagination?.pageSize || 50,
    });

  const handleNavigateToInventory = React.useCallback(
    (ndc: string) => {
      navigate({ to: "/inventory", search: { search: ndc } });
    },
    [navigate]
  );

  const handleNavigateToTransaction = React.useCallback(
    (transactionType: string, transactionId: string) => {
      if (transactionType === "receipt") {
        navigate({ to: `/receipts?id=${transactionId}` });
      } else if (transactionType === "dispense") {
        navigate({ to: `/dispenses?id=${transactionId}` });
      }
    },
    [navigate]
  );

  const columns = React.useMemo(
    () =>
      createActivityColumns(
        onView,
        handleNavigateToInventory,
        handleNavigateToTransaction,
        handleNDCClick,
        handleEditNotes
      ),
    [
      onView,
      handleNavigateToInventory,
      handleNavigateToTransaction,
      handleNDCClick,
      handleEditNotes,
    ]
  );

  // Sync local pagination state with props
  React.useEffect(() => {
    if (pagination) {
      setPaginationState({
        pageIndex: pagination.page - 1,
        pageSize: pagination.pageSize,
      });
    }
  }, [pagination]);

  // Handle pagination changes
  React.useEffect(() => {
    if (pagination && pageIndex + 1 !== pagination.page) {
      onPageChange(pageIndex + 1);
    }
  }, [pageIndex, pagination?.page, onPageChange, pagination]);

  React.useEffect(() => {
    if (pagination && pageSize !== pagination.pageSize) {
      onPageSizeChange(pageSize);
    }
  }, [pageSize, pagination?.pageSize, onPageSizeChange, pagination]);

  const paginationState = React.useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  const table = useReactTable({
    data: transactions || [],
    columns,
    pageCount: pagination?.totalPages || 0,
    state: {
      sorting,
      pagination: paginationState,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPaginationState,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });

  if (isLoading && (!transactions || transactions.length === 0)) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-2">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">
            Loading transactions...
          </p>
        </div>
      </div>
    );
  }

  if (!isLoading && (!transactions || transactions.length === 0)) {
    return (
      <div className="flex items-center justify-center py-20 border-2 border-dashed rounded-lg">
        <div className="flex flex-col items-center gap-2 text-center">
          <p className="text-lg font-semibold">No transactions found</p>
          <p className="text-sm text-muted-foreground max-w-sm">
            Try adjusting your filters or date range to see more results.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="activity-table">
      <DataTable
        table={table}
        getRowTestId={(row) => `activity-row-${row.original.transaction_id}`}
      />
      <DataTablePagination table={table} />

      {selectedNDC && (
        <DrugDetailsDrawer
          open={drugDetailsDrawerOpen}
          onOpenChange={setDrugDetailsDrawerOpen}
          ndc={selectedNDC}
          mode="view"
        />
      )}

      {notesTransaction && (
        <NotesThread
          open={notesOpen}
          onClose={() => {
            setNotesOpen(false);
            setNotesTransaction(null);
          }}
          entityType={
            notesTransaction.transaction_type as
              | "receipt"
              | "dispense"
              | "adjustment"
          }
          entityId={notesTransaction.transaction_id}
          ndc={notesTransaction.ndc}
          itemName={notesTransaction.item_name}
        />
      )}
    </div>
  );
}
